import { Fragment, useState } from "react";
import { Popover, Typography } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import clsx from "clsx";

const SCALE = [1, 2, 3, 4, 5];

function band(score: number): "" | "green" | "amber" | "red" {
    if (score > 14) return "red";
    if (score > 7) return "amber";
    if (score > 0) return "green";
    return "";
}

const useStyles = makeStyles((theme) => ({
    trigger: {
        width: 56,
        height: 40,
        borderRadius: theme.spacing(0.75),
        border: `1px dashed ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        background: "transparent",
        font: "inherit",
        color: theme.palette.text.secondary,
        lineHeight: 1,
        "&:hover": { borderColor: theme.palette.secondary.main },
    },
    triggerScored: {
        border: "none",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1rem",
        "&.green": { background: "#76dd78" },
        "&.amber": { background: "#ffb943" },
        "&.red": { background: "#f37142" },
    },
    placeholder: {
        fontSize: "0.7rem",
        letterSpacing: 0.5,
    },
    popover: { padding: theme.spacing(2) },
    heading: {
        fontSize: "0.75rem",
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    matrix: {
        display: "grid",
        gridTemplateColumns: `20px repeat(5, 40px)`,
        gap: theme.spacing(0.5),
        alignItems: "center",
    },
    axisTick: {
        fontSize: "0.7rem",
        color: theme.palette.text.secondary,
        textAlign: "center",
        lineHeight: 1,
    },
    cell: {
        height: 36,
        borderRadius: theme.spacing(0.5),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        font: "inherit",
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "#00000099",
        border: "2px solid transparent",
        padding: 0,
        "&.green": { background: "#76dd78" },
        "&.amber": { background: "#ffb943" },
        "&.red": { background: "#f37142" },
        "&:hover": { opacity: 0.75 },
        "&:focus-visible": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
    },
    cellSelected: {
        border: `2px solid ${theme.palette.common.black}`,
        color: "#000",
    },
    axisCaption: {
        fontSize: "0.7rem",
        color: theme.palette.text.secondary,
        textAlign: "center",
        marginTop: theme.spacing(0.5),
        gridColumn: "2 / span 5",
    },
    severityCaption: {
        fontSize: "0.7rem",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    result: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
        gap: theme.spacing(2),
    },
    resultBadge: {
        minWidth: 40,
        height: 32,
        borderRadius: theme.spacing(0.5),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        color: "#fff",
        background: "#bbb",
        "&.green": { background: "#76dd78" },
        "&.amber": { background: "#ffb943" },
        "&.red": { background: "#f37142" },
    },
}));

interface RiskScoreCellProps {
    severity: number;
    likelihood: number;
    onSeverityChange: (value: number) => void;
    onLikelihoodChange: (value: number) => void;
    disabled?: boolean;
    ariaLabel?: string;
}

export default function RiskScoreCell({
    severity,
    likelihood,
    onSeverityChange,
    onLikelihoodChange,
    disabled = false,
    ariaLabel = "Set risk score",
}: RiskScoreCellProps) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const scored = severity > 0 && likelihood > 0;
    const score = severity * likelihood;
    const scoreBand = band(score);

    // One click sets both axes and closes, rather than severity then likelihood.
    const pick = (s: number, l: number) => {
        onSeverityChange(s);
        onLikelihoodChange(l);
        setAnchorEl(null);
    };

    // Severity descends so the highest risk sits top-right, as a risk matrix reads.
    const severityRows = [...SCALE].reverse();

    return (
        <>
            <button
                type="button"
                aria-label={ariaLabel}
                disabled={disabled}
                className={clsx(classes.trigger, scored && classes.triggerScored, scored && scoreBand)}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {scored ? (
                    score
                ) : (
                    <Typography component="span" className={classes.placeholder}>
                        S × L
                    </Typography>
                )}
            </button>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <div className={classes.popover}>
                    <Typography className={classes.heading}>Select a risk score</Typography>
                    <Typography className={classes.severityCaption}>Severity</Typography>
                    <div className={classes.matrix}>
                        {severityRows.map((s) => (
                            <Fragment key={`row-${s}`}>
                                <span className={classes.axisTick}>{s}</span>
                                {SCALE.map((l) => {
                                    const cellScore = s * l;
                                    const selected = severity === s && likelihood === l;
                                    return (
                                        <button
                                            type="button"
                                            key={`${s}-${l}`}
                                            aria-label={`Severity ${s}, likelihood ${l}, score ${cellScore}`}
                                            aria-pressed={selected}
                                            className={clsx(classes.cell, band(cellScore), selected && classes.cellSelected)}
                                            onClick={() => pick(s, l)}
                                        >
                                            {cellScore}
                                        </button>
                                    );
                                })}
                            </Fragment>
                        ))}
                        <span className={classes.axisTick} />
                        {SCALE.map((l) => (
                            <span key={`lik-${l}`} className={classes.axisTick}>
                                {l}
                            </span>
                        ))}
                        <Typography component="span" className={classes.axisCaption}>
                            Likelihood
                        </Typography>
                    </div>
                    <div className={classes.result}>
                        <Typography variant="body2" color="textSecondary">
                            {scored ? `Severity ${severity} × Likelihood ${likelihood}` : "Severity × Likelihood"}
                        </Typography>
                        <div className={clsx(classes.resultBadge, scoreBand)}>{scored ? score : "—"}</div>
                    </div>
                </div>
            </Popover>
        </>
    );
}
